import React, { useState, useRef, useEffect } from 'react';
import { ZoomIn, ZoomOut, LayoutTemplate, Move } from 'lucide-react';
import FunnelBackground from './FunnelBackground';
import FunnelStage from './FunnelStage';

import './SalesFunnelWidget.css';
import { useAppContext } from '../../../../context/app.context';
import { updateClientStatusAPI } from '../../../../service/ai-model.service';
import { ToastType } from '../../../../helper/constant';

const SalesFunnelWidget = ({ stages, clients = [], orgName }) => {
    const [companies, setCompanies] = useState([]);
    const { activeDashboardVersion, showAlert } = useAppContext();

    useEffect(() => {
        setCompanies(clients);
    }, [clients]);

    // Canvas State
    const [zoom, setZoom] = useState(0.8); // Start slightly zoomed out to see more
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const lastMousePos = useRef({ x: 0, y: 0 });

    // Dimensions - Increased for "Infinite Canvas" feel
    const STAGE_WIDTH = 600;
    const FUNNEL_HEIGHT = 800;
    const TOTAL_WIDTH = STAGE_WIDTH * stages.length;

    // Max revenue for relative sizing
    const maxRevenue = Math.max(...companies.map(c => c.revenue), 10000);

    const handleDrop = (companyId, stageId, subStageId) => {
        setCompanies(prev => {
            const updated = prev.map(c => {
                if (c.id === companyId) {
                    if (c.stageId !== stageId || c.subStageId !== subStageId) {
                        const params = new URLSearchParams(window.location.search)
                        const companyName = params.get('org');
                        const payload = {
                            companyName,
                            versionId: activeDashboardVersion,
                            clientName: c.name,
                            target: {
                                label: stageId,
                                subLabel: subStageId
                            }
                        }
                        updateClientStatusAPI(payload).then(r => r).catch(err => {
                            showAlert('Failed to update client status', ToastType.ERROR);
                        });
                        return { ...c, stageId, subStageId };
                    }
                }
                return c;
            });
            return updated;
        });
    };

    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2.0));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.4));

    // Wheel Zoom Logic
    const handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
            // Zoom
            e.preventDefault();
            const delta = e.deltaY * -0.001;
            setZoom(prev => Math.min(Math.max(prev + delta, 0.4), 2.0));
        } else {
            // Pan with wheel (if not zooming)
            // e.preventDefault(); // Optional: prevent browser navigation
            setPan(prev => ({
                x: prev.x - e.deltaX,
                y: prev.y - e.deltaY
            }));
        }
    };

    // Pan Logic
    const handleMouseDown = (e) => {
        // Check if we are clicking on a draggable bubble to avoid conflict
        // The bubble's dragStart will prevent this if handled correctly, but 
        // we generally want to pan when clicking the background.
        if ((e.target).closest('[draggable="true"]')) return;

        setIsPanning(true);
        lastMousePos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
        if (!isPanning) return;

        const dx = e.clientX - lastMousePos.current.x;
        const dy = e.clientY - lastMousePos.current.y;

        setPan(prev => ({ x: prev.x + dx, y: prev.y + dy }));
        lastMousePos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
        setIsPanning(false);
    };

    return (
        <div className="app-container">
            {/* Toolbar */}
            <div className="toolbar">
                <div className="toolbar-left">
                    <div className="app-logo-box">
                        <LayoutTemplate className="w-5 h-5 text-white" />
                    </div>
                    <h1 className="app-title capitalize">{orgName}</h1>
                </div>

                <div className="toolbar-right">
                    {/* Stats */}
                    <div className="stats-group">
                        <div style={{ textAlign: 'right' }}>
                            <span className="stats-label">PIPELINE VALUE:</span>
                            <span className="stats-value">
                                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact' }).format(companies.reduce((a, b) => a + b.revenue, 0))}
                            </span>
                        </div>
                    </div>

                    {/* Zoom Controls */}
                    <div className="zoom-controls">
                        <button onClick={handleZoomOut} className="zoom-btn" title="Zoom Out">
                            <ZoomOut size={18} />
                        </button>
                        <span className="zoom-display">{Math.round(zoom * 100)}%</span>
                        <button onClick={handleZoomIn} className="zoom-btn" title="Zoom In">
                            <ZoomIn size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Infinite Canvas */}
            <main
                className={`canvas-main ${isPanning ? 'panning' : 'idle'}`}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onWheel={handleWheel}
            >
                {/* Background Grid Pattern for Context */}
                <div
                    className="background-grid"
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`
                    }}
                />

                {/* Funnel Container */}
                <div
                    className="funnel-container"
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        width: TOTAL_WIDTH,
                        height: FUNNEL_HEIGHT,
                    }}
                >
                    {/* SVG Background Layer */}
                    <FunnelBackground width={TOTAL_WIDTH} height={FUNNEL_HEIGHT} stageWidth={STAGE_WIDTH} />

                    {/* Content Layer (Stages) */}
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', zIndex: 10 }}>
                        {stages.map((stage, index) => (
                            <FunnelStage
                                key={stage.id}
                                index={index}
                                totalStages={stages.length}
                                stage={stage}
                                companies={companies.filter(c => c.stageId === stage.id)}
                                maxRevenue={maxRevenue}
                                onDrop={handleDrop}
                                stageWidth={STAGE_WIDTH}
                                totalHeight={FUNNEL_HEIGHT}
                            />
                        ))}
                    </div>
                </div>

                {/* Pan Indicator Hint */}
                <div className="pan-indicator">
                    <Move size={14} />
                    <span>Click & Drag to Pan • Scroll to Zoom</span>
                </div>
            </main>
        </div>
    );
}

export default SalesFunnelWidget;